"use client"

import { useState } from "react"
import { m, AnimatePresence } from "framer-motion"
import { useLocale } from "next-intl"
import { useRouter } from "next/navigation"
import { cn } from "@/lib/utils"
import { localeLabels } from "@/i18n/config"
import { track } from "@/lib/analytics"
import { useCountry } from "@/components/layout/country-provider"
import { usePickerCountries } from "@/lib/use-preview-mode"
import {
  CountryFlag,
  buildLocaleSwitchUrl,
} from "@/components/ui/navbar/locale-picker"
import type { Country } from "@/i18n/config"

/**
 * Compact locale switcher for the focused quiz chrome.
 *
 * The navbar/footer pickers don't fit here — the navbar isn't rendered in
 * the minimal-chrome layout, and the footer picker opens upward. This is a
 * down-opening variant sized for the chrome header's top-right corner.
 *
 * Switching language reloads the page, so in-progress answers reset. That's
 * acceptable: the only reason to switch mid-quiz is having landed in the
 * wrong language, where starting fresh in the right one is the intent.
 */
export function QuizLocalePicker() {
  const locale = useLocale()
  const countryConfig = useCountry()
  const pickerCountries = usePickerCountries(countryConfig.code)
  const router = useRouter()
  const [open, setOpen] = useState(false)

  function switchLocale(newCountry: string, newLanguage: string) {
    const segments = window.location.pathname.split("/").filter(Boolean)
    const pagePath = segments.slice(2).join("/")
    const currentPath = pagePath ? `/${pagePath}` : ""
    const newPath = buildLocaleSwitchUrl({
      currentPath,
      targetCountry: newCountry as Country,
      targetLocale: newLanguage,
    })
    track.localeChanged({
      surface: "quiz-chrome",
      from_country: countryConfig.code,
      from_locale: locale,
      to_country: newCountry,
      to_locale: newLanguage,
      page_path: window.location.pathname,
    })
    router.push(newPath, { scroll: false })
    document.cookie = `preferred-language=${newLanguage};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`
    setOpen(false)
  }

  const otherLocales = pickerCountries.flatMap((cc) =>
    cc.languages
      .filter((lang) => !(cc.code === countryConfig.code && lang === locale))
      .map((lang) => ({ country: cc, lang }))
  )

  // Single-locale markets have nothing to switch to — hide entirely.
  if (otherLocales.length === 0) return null

  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-neutral-600 transition-colors hover:text-neutral-900"
      >
        <CountryFlag flag={countryConfig.flag} className="w-[14px]" />
        {localeLabels[locale] || "EN"}
        <svg
          width="10"
          height="10"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
          className={cn(
            "transition-transform duration-200",
            open && "rotate-180"
          )}
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {/* safe-animate-presence: conditional-mount — child only renders when `open=true` */}
      <AnimatePresence>
        {open && (
          <m.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full right-0 z-10 mt-1 min-w-[120px] overflow-hidden rounded-[12px] bg-white shadow-lg"
          >
            {otherLocales.map(({ country: cc, lang }) => (
              <button
                key={`${cc.code}-${lang}`}
                type="button"
                onClick={() => switchLocale(cc.code, lang)}
                className="flex w-full cursor-pointer items-center gap-1.5 px-3 py-2 text-left text-sm text-neutral-600 transition-colors hover:bg-neutral-50 hover:text-neutral-900"
              >
                <CountryFlag flag={cc.flag} className="w-[14px]" />
                {localeLabels[lang]}
              </button>
            ))}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}
